import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';

import {
  DataTableColumnGeometryChange,
  DataTableSelectionCellContext,
  DataTableColumn,
  DataTableRow,
  DataTableCellContext,
} from '../data-table.types';

// This is ng-packagr build error workaround
type TableRow = DataTableRow;
type TableCol<Id extends string = string> = DataTableColumn<Id>;

@Component({
  selector: 'rt-data-table',
  styleUrls: ['./data-table.component.less'],
  template: `
    <rt-data-table-geometry
      [autoFit]="autoFit"
      [cols]="cols"
      (geometryChange)="geometryChange.emit($event)"
    >
      <table>
        <thead>
          <tr>
            <th
              *ngIf="selectable"
              [style.width]="selectableColumnWidth + 'px'"
              class="no-border selectable"
            ></th>
            <th
              *ngFor="let col of cols; trackBy: trackCol; let last = last; let first = first"
              [class.is-sortable]="col.options.sortable"
              [class.sort-asc]="col.sort === 'asc'"
              [class.sort-desc]="col.sort === 'desc'"
              [class.has-label]="!!col.label"
              [class.border]="!last"
              [rtColumnResize]="col.id"
              (click)="col.options.sortable && sortChange.emit(col)"
            >
              <div
                class="cell-body"
                [style.left]="selectable && first ? -selectableColumnWidth + 'px' : 'unset'"
              >
                <ng-container
                  *ngTemplateOutlet="
                    columnHeaderTemplate || defaultColumnHeaderTemplate;
                    context: columnHeaderTemplateContexts[col.id]
                  "
                >
                </ng-container>
                <rt-icon
                  *ngIf="col.options.sortable"
                  [size]="'small-x'"
                  [name]="col.sort === 'asc' ? 'sort-ascending' : 'sort-descending'"
                  text-light
                ></rt-icon>
              </div>
            </th>
            <th *ngIf="rowDetailsTemplate" [style.width]="'50px'" class="no-border"></th>
          </tr>
        </thead>
        <tbody>
          <rt-data-table-row
            *rxFor="let row of rows; let index = index; let even = even; trackBy: trackByRow"
            [index]="index"
            [alwaysVisible]="index < rowsToPaintAtStart"
            [hoverable]="hoverable"
            [private]="!!row.private"
            [disabled]="!!row.disabled"
            [cellPadding]="cellPadding"
            [cellTemplates]="cellTemplates"
            [defaultCellTemplate]="defaultCellTemplate || dtDefaultCellTemplate"
            [selectable]="selectable"
            [clickable]="
              clickable === true || (clickable && clickable[row.id]) || !!rowDetailsTemplate
            "
            [selectionCellTemplate]="selectionCellTemplate"
            [row]="row"
            [columnIds]="columnIds"
            [selected]="selectable && selectedIdsDict[row.id] === true"
            [even]="even"
            [isZebra]="hasZebra"
            [rowDetailsTemplate]="rowDetailsTemplate"
            (click)="clickable && !row.disabled ? rowClick.emit(row) : null"
            (rowSelect)="selectable ? rowSelect.emit({ row: row, selected: $event }) : null"
          >
          </rt-data-table-row>
        </tbody>
      </table>
    </rt-data-table-geometry>

    <ng-template #dtDefaultCellTemplate let-row="row" let-columnId="columnId">
      {{ row[columnId] }}
    </ng-template>
    <ng-template #defaultColumnHeaderTemplate let-column="col"> {{ column.label }} </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.clickable]': 'clickable',
    '[class.selectable]': 'selectable',
    '[class.raised]': 'raised',
    '[class.has-smaller-padding]': 'hasSmallerPadding',
  },
})
export class DataTableComponent<Row extends TableRow, Col extends TableCol> {
  @Input() public cols: Col[];
  @Input() public rows: Row[];

  @Input() public cellTemplates?: { [key: string]: TemplateRef<DataTableCellContext> };
  @Input() public rowDetailsTemplate: TemplateRef<any>;
  @Input() public defaultCellTemplate?: TemplateRef<DataTableCellContext>;
  @Input() public selectionCellTemplate?: TemplateRef<DataTableSelectionCellContext>;
  @Input() public columnHeaderTemplate?: TemplateRef<{ col: Col }>;

  @Input() public autoFit: boolean = true;

  @Input() public cellPadding: boolean = true;

  @Input() public raised: boolean = true;
  @Input() public rowsToPaintAtStart: number = Infinity;
  @Input() public lazyPaint: boolean = false;

  @Input() public clickable: boolean | Record<string, boolean> = false;
  @Input() public hoverable: boolean = false;
  @Input() public selectable: boolean = false;
  @Input() public selectedIdsDict: Record<number | string, boolean> = {};
  @Input() public selectableColumnWidth: number = 32;
  @Input() public hasZebra: boolean = true;

  @Input() public hasSmallerPadding: boolean = false;

  @Output() public sortChange: EventEmitter<Col> = new EventEmitter();
  @Output() public rowClick: EventEmitter<Row> = new EventEmitter();
  @Output()
  public rowSelect: EventEmitter<{ row: Row; selected: boolean }> = new EventEmitter();
  @Output() public geometryChange: EventEmitter<DataTableColumnGeometryChange> = new EventEmitter();

  public columnIds: string[] = [];
  public columnHeaderTemplateContexts: { [key: string]: { col: Col } } = {};

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.cols) {
      const columnIdsChanged =
        changes.cols.firstChange ||
        changes.cols.previousValue.map((col: Col) => col.id).join('#') !==
          changes.cols.currentValue.map((col: Col) => col.id).join('#');
      if (columnIdsChanged) {
        this.columnIds = this.cols.map(col => col.id);
      }

      this.columnHeaderTemplateContexts = this.cols.reduce(
        (acc, col) => ({ ...acc, [col.id]: { col } }),
        {},
      );
    }
  }

  public trackCol(index: number, entity: Col): string {
    return entity.id;
  }

  public trackByRow(index: number, row: Row): Row['id'] {
    return row.id;
  }
}
